import { useState } from "react";
import { useAuthStore } from "@/features/auth";
import { useCreditsStore } from "@/features/credits";

interface CreditGateProps {
  dialogId: string;
  dialogTitle: string;
  onUnlock: () => void;
}

export function CreditGate({ dialogId, dialogTitle, onUnlock }: CreditGateProps) {
  const user = useAuthStore((s) => s.user);
  const emailUser = useAuthStore((s) => s.emailUser);
  const openModal = useAuthStore((s) => s.openModal);
  const openEmailModal = useAuthStore((s) => s.openEmailModal);
  const deductCredit = useCreditsStore((s) => s.deductCredit);
  const deducting = useCreditsStore((s) => s.deducting);
  const [error, setError] = useState<string | null>(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);

  async function handleUnlock() {
    setError(null);
    const result = await deductCredit(dialogId, dialogTitle);
    if (result.success) {
      onUnlock();
    } else if (result.error === "Insufficient credits") {
      setInsufficientCredits(true);
    } else {
      setError(result.error ?? "Something went wrong");
    }
  }

  return (
    <div className="relative mt-2">
      <div
        className="absolute -top-24 left-0 right-0 h-24 pointer-events-none"
        style={{ background: "linear-gradient(to bottom, transparent, var(--c-bg))" }}
      />
      <div className="bg-th-surface border border-th-border rounded-xl p-8 text-center">
        <div className="text-3xl mb-3">{insufficientCredits ? "💳" : "🔒"}</div>
        <h3 className="text-lg font-bold text-white mb-2">
          {insufficientCredits ? "No Credits Remaining" : "Preview Complete"}
        </h3>
        <p className="text-sm text-slate-400 mb-6 max-w-sm mx-auto">
          {!user && !emailUser
            ? "Enter your email to start reading full dialogs for free."
            : !user && emailUser
              ? "Create an account to unlock dialogs with credits, notes, and role-play mode."
              : insufficientCredits
                ? "Purchase more credits to continue unlocking dialogs."
                : "Use 1 credit to unlock the full dialog, including all lines, notes, and role-play."}
        </p>

        {error && (
          <p className="text-sm text-red-400 mb-4">{error}</p>
        )}

        {!user && !emailUser ? (
          <button
            onClick={openEmailModal}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-indigo-500/25"
          >
            Get Started Free
          </button>
        ) : !user && emailUser ? (
          <button
            onClick={openModal}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-indigo-500/25"
          >
            Create Account to Unlock
          </button>
        ) : insufficientCredits ? (
          <a
            href="https://glossika.com/credits"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-amber-500/25"
          >
            Buy Credits
          </a>
        ) : (
          <button
            onClick={handleUnlock}
            disabled={deducting}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-indigo-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deducting ? "Unlocking..." : "Use 1 Credit to Unlock"}
          </button>
        )}
      </div>
    </div>
  );
}
